import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Select, Modal, Form, Tag, Space, Card,
  Row, Col, Statistic, Popconfirm, message, Empty, InputNumber, DatePicker,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SettingOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getPreschoolSeasons, createPreschoolSeason, updatePreschoolSeason, deletePreschoolSeason,
  getPreschools, createPreschool, updatePreschool, deletePreschool,
  createAgreement, getSeasonSummary,
} from '../api/preschools';
import useServerTable from '../hooks/useServerTable';
import useDebounce from '../hooks/useDebounce';
import useAuthStore from '../store/authStore';
import { formatMoney } from '../utils/money';
import { apiErrorMessage } from '../utils/apiError';
import BranchSelect from '../components/common/BranchSelect';
import { getBranches } from '../api/branches';
import PreschoolCertificateTemplates from '../components/preschool/PreschoolCertificateTemplates';

/**
 * Anaokulu Takip — kurum listesi ve sezon yönetimi.
 *
 * Ekranın omurgası SEZON: anlaşma okula değil sezona ait, "bu sezon hangi
 * okullarla çalışıyoruz" modülün en sık sorulan sorusu. Bu yüzden sezon
 * seçici en üstte ve seçim listeyi süzüyor.
 *
 * Anlaşma tutarı yalnızca `preschools.finance` yetkisi olanlara görünür —
 * backend de o alanları yanıttan düşürüyor, buradaki gizleme ikinci katman.
 */
const Preschools = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const izinler = user?.permissions || [];
  const finansVar = izinler.includes('preschools.finance');

  const [seasons, setSeasons] = useState([]);
  // BranchSelect subeleri KENDI CEKMIYOR, prop bekliyor. Gecilmezse liste
  // bos gelir ve sube hic secilemez (Eren Bey bildirdi, 07.10.2026).
  const [branches, setBranches] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [ozet, setOzet] = useState(null);

  const [okulModal, setOkulModal] = useState(false);
  // null = ekleme, kayit = duzenleme. Ayni modal iki isi birden goruyor;
  // ikinci bir form yazmak ayni alanlarin iki yerde bakimi demekti.
  const [duzenlenenOkul, setDuzenlenenOkul] = useState(null);
  const [duzenlenenSezon, setDuzenlenenSezon] = useState(null);
  const [sezonModal, setSezonModal] = useState(false);
  const [sablonModal, setSablonModal] = useState(false);
  const [anlasmaModal, setAnlasmaModal] = useState(null);   // seçili okul
  const [saving, setSaving] = useState(false);

  const [okulForm] = Form.useForm();
  const [sezonForm] = Form.useForm();
  const [anlasmaForm] = Form.useForm();

  const table = useServerTable('preschools', 20);

  useEffect(() => {
    getBranches({ per_page: 100 })
      .then(({ data }) => setBranches(data.data || []))
      .catch(() => {});

    getPreschoolSeasons()
      .then(({ data }) => {
        setSeasons(data || []);
        // Varsayılan: aktif sezonların en yenisi. Kullanıcı neredeyse her
        // zaman bu sezona bakıyor.
        const aktif = (data || []).find((s) => s.is_active) || (data || [])[0];
        setSeasonId(aktif?.id ?? null);
      })
      .catch(() => {});
  }, []);

  const fetchRows = useCallback(async (o = {}) => {
    setLoading(true);
    try {
      const { data } = await getPreschools({
        ...table.params(o),
        search: debouncedSearch || undefined,
        season_id: seasonId || undefined,
      });
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch {
      message.error('Anaokulu listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // useCallback bagimliliklari: filtreler eksik kalirsa sayfa degisince
    // sessizce sifirlanir (CLAUDE.md - stale closure).
  }, [table.page, table.pageSize, table.sorter, debouncedSearch, seasonId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const fetchOzet = useCallback(() => {
    if (!seasonId || !finansVar) { setOzet(null); return; }
    getSeasonSummary({ season_id: seasonId })
      .then(({ data }) => setOzet(data.ozet))
      .catch(() => setOzet(null));
  }, [seasonId, finansVar]);

  useEffect(() => { fetchOzet(); }, [fetchOzet]);

  /** Modalı ekleme ya da DÜZENLEME için açar. `kayit` varsa düzenleme. */
  const okulModalAc = (kayit = null) => {
    setDuzenlenenOkul(kayit);
    okulForm.resetFields();
    if (kayit) okulForm.setFieldsValue(kayit);
    setOkulModal(true);
  };

  const handleOkulKaydet = async (values) => {
    setSaving(true);
    try {
      if (duzenlenenOkul) {
        await updatePreschool(duzenlenenOkul.id, values);
        message.success('Anaokulu güncellendi.');
      } else {
        await createPreschool(values);
        message.success('Anaokulu eklendi.');
      }
      setOkulModal(false);
      setDuzenlenenOkul(null);
      okulForm.resetFields();
      fetchRows();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const sezonDuzenlemeyeAl = (s) => {
    setDuzenlenenSezon(s);
    sezonForm.setFieldsValue({
      name: s.name,
      range: [dayjs(s.start_date), dayjs(s.end_date)],
    });
  };

  const handleSezonKaydet = async (values) => {
    setSaving(true);
    try {
      const govde = {
        name: values.name,
        // API'ye giden her tarih .format('YYYY-MM-DD') - ham dayjs nesnesi
        // gonderilirse UTC'ye cevrilirken bir gun geriye kayar (CLAUDE.md).
        start_date: values.range[0].format('YYYY-MM-DD'),
        end_date: values.range[1].format('YYYY-MM-DD'),
      };

      let yeniId = duzenlenenSezon?.id;
      if (duzenlenenSezon) {
        await updatePreschoolSeason(duzenlenenSezon.id, govde);
        message.success('Sezon güncellendi.');
      } else {
        const { data } = await createPreschoolSeason(govde);
        yeniId = data.id;
        message.success('Sezon eklendi.');
      }

      setDuzenlenenSezon(null);
      sezonForm.resetFields();
      const { data: liste } = await getPreschoolSeasons();
      setSeasons(liste || []);
      setSeasonId(yeniId);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAnlasmaEkle = async (values) => {
    setSaving(true);
    try {
      const { data } = await createAgreement(anlasmaModal.id, {
        preschool_season_id: seasonId,
        monthly_fee: values.monthly_fee,
        payment_day: values.payment_day,
        certificate_term_count: values.certificate_term_count,
      });
      message.success('Anlaşma oluşturuldu.');
      setAnlasmaModal(null);
      anlasmaForm.resetFields();
      navigate(`/preschools/agreements/${data.id}`);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Anlaşma oluşturulamadı.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Anaokulu',
      dataIndex: 'name',
      render: (_, r) => (
        <div>
          <span className="font-medium">{r.name}</span>
          {!r.is_active && <Tag color="default" className="ml-2">Pasif</Tag>}
          {(r.district || r.city) && (
            <div className="text-xs text-gray-400">
              {[r.district, r.city].filter(Boolean).join(' / ')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'İletişim',
      dataIndex: 'contact_name',
      render: (_, r) => (
        <div>
          <div>{r.contact_name || '—'}</div>
          {r.contact_phone && <div className="text-xs text-gray-400">{r.contact_phone}</div>}
        </div>
      ),
    },
    {
      title: 'Bu Sezon',
      key: 'anlasma',
      render: (_, r) => {
        if (!seasonId) return <span className="text-gray-400">Sezon seçin</span>;
        if (!r.season_agreement) {
          return (
            <Button size="small" onClick={() => { setAnlasmaModal(r); anlasmaForm.resetFields(); }}>
              Anlaşma Ekle
            </Button>
          );
        }
        const a = r.season_agreement;
        return (
          <Space direction="vertical" size={0}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => navigate(`/preschools/agreements/${a.id}`)}
            >
              Anlaşmayı Aç
            </Button>
            {finansVar && a.monthly_fee !== undefined && (
              <span className="text-xs text-gray-500">
                Aylık {formatMoney(a.monthly_fee)} · her ayın {a.payment_day}. günü
              </span>
            )}
            {a.end_date && <Tag color="orange">Yarıda kesildi</Tag>}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'islem',
      width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => okulModalAc(r)} />
          <Popconfirm
            title="Anaokulu silinsin mi?"
            description="Anlaşmalar, sınıflar ve çocuklar da erişilemez olur."
            onConfirm={async () => {
              try {
                await deletePreschool(r.id);
                message.success('Anaokulu silindi.');
                fetchRows();
              } catch (error) {
                message.error(apiErrorMessage(error, 'Silinemedi.'));
              }
            }}
            okText="Sil"
            cancelText="Vazgeç"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h2 className="text-2xl font-bold mb-0">Anaokulu Takip</h2>
        <Space wrap>
          <Button icon={<FileTextOutlined />} onClick={() => setSablonModal(true)}>
            Sertifika Şablonları
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => setSezonModal(true)}>
            Sezonlar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => okulModalAc()}>
            Anaokulu Ekle
          </Button>
        </Space>
      </div>

      <Card size="small" className="mb-4">
        <Space wrap>
          <Select
            style={{ minWidth: 220 }}
            placeholder="Sezon"
            value={seasonId}
            onChange={setSeasonId}
            options={seasons.map((s) => ({
              value: s.id,
              label: s.name + (s.is_active ? '' : ' (pasif)'),
            }))}
            notFoundContent={<Empty description="Sezon tanımlı değil" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          />
          <Input.Search
            allowClear
            placeholder="Anaokulu ara..."
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Space>
      </Card>

      {ozet && (
        <Row gutter={16} className="mb-4">
          <Col xs={12} md={6}><Card size="small"><Statistic title="Okul" value={ozet.okul_sayisi} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Planlanan" value={formatMoney(ozet.toplam_tutar)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Tahsil Edilen" value={formatMoney(ozet.tahsil)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Kalan" value={formatMoney(ozet.kalan)} /></Card></Col>
        </Row>
      )}

      <Table
        rowKey="id"
        dataSource={rows}
        loading={loading}
        size="small"
        columns={columns.map((c) => ({ ...c, ...table.sortProps(c.dataIndex) }))}
        {...table.tableProps(total, fetchRows)}
      />

      {/* --- Anaokulu ekle / duzenle (ayni modal) --- */}
      <Modal
        title={duzenlenenOkul ? 'Anaokulu Düzenle' : 'Anaokulu Ekle'}
        open={okulModal}
        onCancel={() => { setOkulModal(false); setDuzenlenenOkul(null); okulForm.resetFields(); }}
        footer={null}
      >
        <Form form={okulForm} layout="vertical" onFinish={handleOkulKaydet}>
          <Form.Item name="name" label="Anaokulu Adı" rules={[{ required: true, message: 'Ad giriniz.' }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="branch_id" label="Şube">
            <BranchSelect branches={branches} placeholder="Şube seçin (boş = yalnızca yöneticiler)" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="district" label="İlçe"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="city" label="İl"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="contact_name" label="Yetkili"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="contact_phone" label="Telefon"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="notes" label="Not"><Input.TextArea rows={2} /></Form.Item>
          {/*
            Haftalık hazır mesaj KURUMDA duruyor, anlaşmada değil —
            anlaşma sezonluk, mesaj her sezon baştan yazılmasın diye.

            ⚠ Uygulama kimseye mesaj GÖNDERMİYOR, gönderemez de: anaokulu
            çocuklarının velisinin telefonu sistemde yok ve olmaması
            bilinçli. Akış: öğretmen kopyalar → okuldaki yetkiliye atar →
            yetkili velilere iletir. Buraya "gönder" düğmesi ekleme.
          */}
          <Form.Item
            name="whatsapp_message"
            label="Haftalık WhatsApp Mesajı"
            extra={'Ders Günleri sekmesinde her satırda "Mesajı Kopyala" olarak çıkar. '
              + '{konu} ve {tarih} yazarsanız o dersin konusu ve tarihi ile değiştirilir.'}
          >
            <Input.TextArea rows={3} placeholder="Örn: Merhaba, bu hafta çocuklarımızla {konu} çalıştık." />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Kaydet</Button>
        </Form>
      </Modal>

      {/* --- Sezonlar --- */}
      <Modal
        title="Sezonlar"
        open={sezonModal}
        onCancel={() => setSezonModal(false)}
        footer={null}
        width={620}
      >
        <p className="text-gray-500 text-sm mb-3">
          Anlaşmalar sezona bağlı. Bir okulla bu sezon çalışıp gelecek sezon
          çalışmamak, o sezon için anlaşma açmamak demek.
        </p>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={seasons}
          columns={[
            { title: 'Sezon', dataIndex: 'name' },
            {
              title: 'Tarih',
              key: 'tarih',
              render: (_, r) => `${dayjs(r.start_date).format('DD.MM.YYYY')} — ${dayjs(r.end_date).format('DD.MM.YYYY')}`,
            },
            {
              title: '',
              key: 'islem',
              width: 90,
              render: (_, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => sezonDuzenlemeyeAl(r)} />
                  <Popconfirm
                    title="Sezon silinsin mi?"
                    onConfirm={async () => {
                      try {
                        await deletePreschoolSeason(r.id);
                        message.success('Sezon silindi.');
                        const { data } = await getPreschoolSeasons();
                        setSeasons(data || []);
                        if (seasonId === r.id) setSeasonId((data || [])[0]?.id ?? null);
                      } catch (error) {
                        message.error(apiErrorMessage(error, 'Silinemedi.'));
                      }
                    }}
                    okText="Sil"
                    cancelText="Vazgeç"
                  >
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
        <Form form={sezonForm} layout="vertical" onFinish={handleSezonKaydet} className="mt-4">
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item
                name="name"
                label={duzenlenenSezon ? 'Sezonu Düzenle' : 'Yeni Sezon'}
                rules={[{ required: true, message: 'Ad giriniz.' }]}
              >
                <Input placeholder="2026-2027 Sezonu" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="range" label="Tarih Aralığı" rules={[{ required: true, message: 'Aralık seçiniz.' }]}>
                <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {duzenlenenSezon ? 'Kaydet' : 'Sezon Ekle'}
            </Button>
            {duzenlenenSezon && (
              <Button onClick={() => { setDuzenlenenSezon(null); sezonForm.resetFields(); }}>
                Vazgeç
              </Button>
            )}
          </Space>
        </Form>
      </Modal>

      {/* --- Anlaşma ekle --- */}
      <Modal
        title={anlasmaModal ? `${anlasmaModal.name} — Anlaşma` : 'Anlaşma'}
        open={!!anlasmaModal}
        onCancel={() => { setAnlasmaModal(null); anlasmaForm.resetFields(); }}
        footer={null}
      >
        <p className="text-gray-500 text-sm mb-4">
          Seçili sezon için anlaşma oluşturulur. Aylık tutar sabittir;
          yapılamayan ders tutarı değiştirmez.
        </p>
        <Form
          form={anlasmaForm}
          layout="vertical"
          onFinish={handleAnlasmaEkle}
          initialValues={{ payment_day: 10, certificate_term_count: 2 }}
        >
          <Form.Item name="monthly_fee" label="Aylık Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="payment_day" label="Ödeme Günü (ayın kaçı)" rules={[{ required: true }]}>
            <InputNumber min={1} max={31} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="certificate_term_count" label="Sezonda Kaç Sertifika" rules={[{ required: true }]}>
            <Select options={[1, 2, 3, 4].map((n) => ({ value: n, label: `${n} sertifika` }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Oluştur</Button>
        </Form>
      </Modal>

      <PreschoolCertificateTemplates open={sablonModal} onClose={() => setSablonModal(false)} />
    </div>
  );
};

export default Preschools;
